/**
 * A metade que LÊ o "Personalizar Home" — ligada em 07/08/2026.
 *
 * ── O defeito que isto conserta (MAPA-FUNCIONAL-V2 §6.12)
 *
 * O builder (`/admin/dashboard/builder`) gravava o layout em
 * `user_dashboard_layouts` desde a migration 128, e a `HomePage` montava
 * widgets fixos por import — **nunca lia o que tinha sido salvo**. A metade que
 * grava funcionava; a que lê nunca foi escrita. Os 2 layouts salvos no beta
 * (Matheus e a conta financeiro@) nunca surtiram efeito para ninguém.
 *
 * ── Por que ADITIVO, e não substituindo a Home
 *
 * Os dois layouts salvos têm **um widget cada** (`acao-3` e `kpi-2`). Se a Home
 * passasse a ser "só o layout", essas duas pessoas abririam o sistema e
 * perderiam saudação, KPIs, agenda, atividades e aprovações — uma regressão
 * visível, causada por um layout que foi salvo em teste e nunca teve efeito.
 *
 * Então os widgets escolhidos entram como uma SEÇÃO A MAIS, embaixo do que já
 * existe. Nada sai. Se a Home deve virar exclusivamente o layout, isso é
 * decisão de produto que depende do padrão por perfil (a camada seguinte) —
 * não se decide aqui, de lado, para caber numa entrega.
 *
 * ── Quem não usa o builder não vê nada
 *
 * Sem layout salvo, a seção não renderiza — nem título, nem espaço em branco.
 * Ninguém ganha um bloco vazio por causa de uma funcionalidade que não usa.
 */
import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import {
  fetchLayoutAtivo,
  fetchWidgetsCatalog,
  type LayoutItem,
  type Layouts,
  type Widget,
} from '@/lib/dashboard/layouts-api';
import type { PerfilUsuario } from '@/widgets/registry';
import { WidgetRenderer } from '@/widgets/WidgetRenderer';

/**
 * O perfil que o registry entende. O catálogo já devolve o perfil resolvido
 * pelo SERVIDOR (`GET /dashboard/widgets` → `{ items, perfil }`), e é ele que
 * mandamos ao renderer — perguntar de novo no cliente abriria a chance de os
 * dois discordarem.
 *
 * ⚠️ Família do #77: `perfil` aqui é o do catálogo de widgets, NÃO o
 * departamento. Quem escolhe QUAL home aparece continua sendo
 * `escolherHomeVariant`, que olha departamento — é por isso que a home do
 * Kevyn (perfil `comercial`, departamento `orcamentista`) funciona. Nada aqui
 * muda aquela regra.
 */
function perfilDoCatalogo(perfil: string | undefined, isAdmin: boolean): PerfilUsuario {
  const conhecidos: PerfilUsuario[] = [
    'admin',
    'comercial',
    'administrativo',
    'tecnico',
    'cliente_gestor',
    'cliente_portaria',
  ];
  if (perfil && (conhecidos as string[]).includes(perfil)) return perfil as PerfilUsuario;
  return isAdmin ? 'admin' : 'administrativo';
}

/** Ordena como o builder desenhou: de cima para baixo, da esquerda para a direita. */
function ordenarComoNoBuilder(itens: LayoutItem[]): LayoutItem[] {
  return [...itens].sort((a, b) => a.y - b.y || a.x - b.x);
}

/**
 * O layout guarda vários breakpoints (`lg`, `md`, `sm`, `xs`, `xxs`). Aqui a
 * grade é responsiva por CSS, então o que importa é a ORDEM — e ela vem do
 * primeiro breakpoint que a pessoa realmente montou.
 */
function itensDoLayout(layouts: Layouts | null): LayoutItem[] {
  if (!layouts) return [];
  for (const bp of ['lg', 'md', 'sm', 'xs', 'xxs'] as const) {
    const itens = layouts[bp];
    if (itens && itens.length > 0) return ordenarComoNoBuilder(itens);
  }
  return [];
}

export function WidgetsPersonalizadosSection() {
  const { user } = useAuth();

  const layoutQuery = useQuery({
    queryKey: ['dashboard', 'layout', 'ativo'],
    queryFn: fetchLayoutAtivo,
    staleTime: 60_000,
  });
  const catalogoQuery = useQuery({
    queryKey: ['dashboard', 'widgets', 'catalogo'],
    queryFn: fetchWidgetsCatalog,
    staleTime: 5 * 60_000,
  });

  // Silêncio proposital nos dois casos: sem layout salvo não há seção, e
  // enquanto carrega não se anuncia um bloco que pode não existir.
  if (layoutQuery.isLoading || catalogoQuery.isLoading) return null;
  if (layoutQuery.isError || catalogoQuery.isError) return null;

  const bruto = layoutQuery.data?.layout?.layout_json;
  if (!bruto) return null;

  let layouts: Layouts | null = null;
  try {
    layouts = JSON.parse(bruto) as Layouts;
  } catch {
    // Layout ilegível não derruba a Home nem acusa o usuário de nada: a seção
    // some e o resto da tela segue.
    return null;
  }

  const itens = itensDoLayout(layouts);
  if (itens.length === 0) return null;

  const catalogo = catalogoQuery.data;
  const porKey = new Map<string, Widget>((catalogo?.items ?? []).map((w) => [w.key, w]));
  const perfil = perfilDoCatalogo(catalogo?.perfil, user?.isAdmin === true);

  // Widget que saiu do catálogo (ou que o perfil desta pessoa não alcança) é
  // PULADO — não vira card de erro. O catálogo já vem filtrado por perfil pelo
  // servidor, então esta é a mesma régua de permissão, não uma segunda.
  const visiveis = itens.map((i) => porKey.get(i.i)).filter((w): w is Widget => Boolean(w));
  if (visiveis.length === 0) return null;

  return (
    <section className="mb-6" data-testid="widgets-personalizados">
      <h2 className="eyebrow mb-3">Seus widgets</h2>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {visiveis.map((w) => (
          <div key={w.key} className="min-h-[180px]" data-testid={`widget-slot-${w.key}`}>
            <WidgetRenderer widget={w} perfilUsuario={perfil} isEditing={false} />
          </div>
        ))}
      </div>
    </section>
  );
}
